import { parseDefaultInstruction } from './defaultAnalyzer.js';
import { parseReadInstruction } from './readAnalyzer.js';

const LOGIC_KEYWORDS = [
  'IF',
  'THEN',
  'ELSE',
  'ENDIF',
  'CALL',
  'GOTO',
  'EXIT',
  'REQUIRE',
  'READ',
  'COMPUTE',
  'DEFAULT',
  'ENDWINDOW',
  'ENDMENU',
  'WRITE',
];

const SABRE_DELIMITER_PATTERN = /^[\u00af\u00a4\ufffd]/;
const SABRE_SPECIAL_SEQUENCE = '\u00A4\u00A5\u00B6\u00A4';
const SABRE_SPECIAL_SEQUENCE_PATTERN = /\u00A4\u00A5\u00B6\u00A4/g;
const SSR_PATTERN = /^(?:3SSR\b|SSR\b|3[A-Z0-9])/i;
const SUBROUTINE_PATTERN = /^SUB\s+([A-Z0-9_]+):?$/i;
const BUTTON_DEFINITION_PATTERN = /^\[(@BTN_L[A-Z0-9_]+)\]\s*=\s*"([^"]*)"\s*$/i;
const LABEL_PATTERN = /^([A-Z][A-Z0-9_ ]*):$/i;

export function normalizeSstLine(rawLine) {
  return rawLine.trim().replace(/\s+/g, ' ');
}

function cleanVisibleText(value = '') {
  return value.trim().replace(/^["']|["']$/g, '').replace(/\s*:\s*$/, '');
}

function stripTechnicalSuffix(variableName = '') {
  return variableName.replace(/#[A-Z]$/i, '');
}

function parseTechnicalSuffix(variableName = '') {
  const match = variableName.match(/^(.*?)(#[A-Z])$/i);

  return match
    ? {
        target: match[1],
        suffix: match[2].toUpperCase(),
        sourceVariable: variableName,
      }
    : {
        target: variableName,
        suffix: null,
        sourceVariable: variableName,
      };
}

function extractQuotedText(raw) {
  const match = raw.match(/"([^"]*)"/);
  return match ? match[1] : null;
}

function isFormatLine(raw = '') {
  const compact = raw.replace(/\s+/g, '');

  return /^\^+(?:[A-Z]+\*)?\^*$/i.test(compact);
}

function parseSabreSpecialSequence(raw = '') {
  if (!raw.includes(SABRE_SPECIAL_SEQUENCE)) {
    return null;
  }

  const remainder = raw.replace(SABRE_SPECIAL_SEQUENCE_PATTERN, '').trim();

  if (remainder) {
    return null;
  }

  return {
    type: 'sabre_special_sequence',
    sequence: SABRE_SPECIAL_SEQUENCE,
    sequences: [
      {
        sequence: SABRE_SPECIAL_SEQUENCE,
        meaning: 'sabre_special_sequence',
        visible: false,
      },
    ],
    visible: false,
    parseStatus: 'parsed',
    raw,
  };
}

function parseDefine(raw) {
  const match = raw.match(/^DEFINE\s*\[([A-Z0-9_#]+)=([^\]]*)\]$/i);

  if (!match) {
    return {
      type: 'define',
      visible: false,
      parseStatus: 'unknown',
      raw,
      diagnostic: {
        level: 'warning',
        code: 'DEFINE_PARSE_UNCERTAIN',
        message: 'DEFINE no pudo interpretarse completamente',
      },
    };
  }

  const targetInfo = parseTechnicalSuffix(match[1]);
  const parts = match[2].split(':');
  const datatype = parts[0] || null;
  const length = /^\d+$/.test(parts[1] || '') ? Number(parts[1]) : null;
  const required = /^MAND$/i.test(parts[2] || '');
  const label = (parts.slice(5).join(':') || '').trim() || null;

  return {
    type: 'define',
    target: targetInfo.target,
    suffix: targetInfo.suffix,
    sourceVariable: targetInfo.sourceVariable,
    datatype,
    length,
    required,
    label,
    visible: false,
    parseStatus: 'parsed',
    raw,
  };
}

function parseButtonDefinition(raw) {
  const match = raw.match(BUTTON_DEFINITION_PATTERN);

  if (!match) {
    return null;
  }

  return {
    type: 'button_definition',
    button: match[1],
    label: match[2],
    visible: false,
    parseStatus: 'parsed',
    raw,
  };
}

function parseLabel(raw) {
  const match = raw.match(LABEL_PATTERN);

  if (!match) {
    return null;
  }

  return {
    type: 'label',
    name: match[1].trim(),
    visible: false,
    parseStatus: 'parsed',
    raw,
  };
}

function stripSabreDelimiters(raw = '') {
  const normalizedRaw = raw.trim().replace(/\u00c2(?=[\u00a4\u00af\u00ae\ufffd])/g, '');

  if (!SABRE_DELIMITER_PATTERN.test(normalizedRaw)) {
    return null;
  }

  return normalizedRaw.slice(1).replace(/[\u00ae\u00a4\ufffd]+$/g, '');
}

function extractSabreCommandBody(raw = '') {
  return stripSabreDelimiters(raw);
}

function normalizeCommandTemplate(commandBody = '') {
  return commandBody
    .replace(/\{ENTER\}/gi, '')
    .replace(/[\u009d\u00a4\ufffd]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/\[(@?[A-Z0-9_#]+)\]/gi, (_match, variableName) => `{${stripTechnicalSuffix(variableName)}}`)
    .trim();
}

function isRemarkCommand(template = '') {
  return /^(?:5H(?:[-/.])?|5\.|5\*|5A|5U)/i.test(template);
}

function isSsrCommand(template = '') {
  return SSR_PATTERN.test(template);
}

function parseCommandOrRemark(raw) {
  const commandBody = extractSabreCommandBody(raw);

  if (!commandBody) {
    return null;
  }

  const template = normalizeCommandTemplate(commandBody);

  if (isRemarkCommand(template)) {
    return {
      type: 'remark',
      remarkType: 'Historical',
      template,
      visible: false,
      skip: true,
      raw,
    };
  }

  if (isSsrCommand(template)) {
    return {
      type: 'ssr',
      template,
      visible: false,
      raw,
    };
  }

  return {
    type: 'command',
    template,
    visible: false,
    raw,
  };
}

function parseRead(raw) {
  return parseReadInstruction(raw);
}

function parseDefault(raw) {
  return parseDefaultInstruction(raw);
}

function parseCompute(raw) {
  const match = raw.match(/^COMPUTE\s+\[([A-Z0-9_#]+)\]\s*=\s*(.+)$/i);

  return {
    type: 'compute',
    target: match ? stripTechnicalSuffix(match[1]) : null,
    expression: match
      ? match[2].replace(/\[([A-Z0-9_#]+)\]/gi, (_token, variableName) => stripTechnicalSuffix(variableName)).replace(/\s+/g, '')
      : null,
    visible: false,
    raw,
  };
}

function parseLogic(raw, keyword) {
  if (keyword === 'READ') {
    return parseRead(raw);
  }

  if (keyword === 'DEFAULT') {
    return parseDefault(raw);
  }

  if (keyword === 'COMPUTE') {
    return parseCompute(raw);
  }

  return {
    type: 'logic',
    keyword,
    visible: false,
    raw,
  };
}

function detectKeyword(raw) {
  const upper = raw.toUpperCase();
  return LOGIC_KEYWORDS.find((keyword) => {
    return upper === keyword || upper.startsWith(`${keyword} `) || upper.startsWith(`${keyword}[`);
  });
}

function classifyMenuText(raw) {
  const quotedText = extractQuotedText(raw);
  const text = cleanVisibleText(quotedText ?? raw);

  if (/^-+$/.test(text)) {
    return {
      type: 'menu_separator',
      visible: false,
      raw,
    };
  }

  return {
    type: 'menu_option',
    text,
    visible: Boolean(text),
    raw,
  };
}

export function classifySstLine(rawLine, context = {}) {
  const raw = normalizeSstLine(rawLine);

  if (!raw) {
    return {
      type: 'blank',
      visible: false,
      raw,
    };
  }

  const sabreSpecialSequence = parseSabreSpecialSequence(raw);

  if (sabreSpecialSequence) {
    return sabreSpecialSequence;
  }

  if (isFormatLine(raw)) {
    return {
      type: 'format',
      visible: false,
      raw,
    };
  }

  if (/^DEFINE\s*\[/i.test(raw)) {
    return parseDefine(raw);
  }

  const buttonDefinition = parseButtonDefinition(raw);

  if (buttonDefinition) {
    return buttonDefinition;
  }

  const commandOrRemark = parseCommandOrRemark(raw);

  if (commandOrRemark) {
    return commandOrRemark;
  }

  if (SSR_PATTERN.test(raw)) {
    return {
      type: 'ssr',
      visible: false,
      raw,
    };
  }

  const subroutineMatch = raw.match(SUBROUTINE_PATTERN);

  if (subroutineMatch) {
    return {
      type: 'subroutine',
      name: subroutineMatch[1],
      visible: false,
      children: [],
      raw,
    };
  }

  if (/^WINDOW\b/i.test(raw)) {
    return {
      type: 'window',
      title: extractQuotedText(raw),
      visible: true,
      raw,
    };
  }

  if (/^MENU\b/i.test(raw)) {
    return {
      type: 'menu',
      title: extractQuotedText(raw),
      visible: true,
      raw,
    };
  }

  if (/^(REM|REMARK)\b/i.test(raw)) {
    return {
      type: 'remark',
      remarkType: 'Historical',
      template: raw.replace(/^(REM|REMARK)\b\s*/i, ''),
      visible: false,
      skip: true,
      raw,
    };
  }

  const keyword = detectKeyword(raw);

  if (keyword) {
    return parseLogic(raw, keyword);
  }

  if (!context.inWindow && !context.inMenu) {
    const label = parseLabel(raw);

    if (label) {
      return label;
    }
  }

  if (context.inMenu) {
    return classifyMenuText(raw);
  }

  if (context.inWindow) {
    return {
      type: 'visible',
      text: cleanVisibleText(extractQuotedText(raw) ?? raw),
      visible: Boolean(cleanVisibleText(extractQuotedText(raw) ?? raw)),
      raw,
    };
  }

  return {
    type: 'unknown',
    visible: false,
    diagnostic: true,
    raw,
  };
}
